import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { log } from '../../app/logger';
import { recorder } from '../../app/recorder';
import { isResumable } from '../../app/resume';
import { syncWakeLock } from '../../app/wakeLock';
import { MATCH_STATE } from '../../domain/machine';
import { useCaptureClock } from '../../ui/capture/useCaptureClock';
import { BottomBandB } from './BottomBandB';
import { NotificationStripB } from './NotificationStripB';
import { TransitionControlB } from './TransitionControlB';
import type { UndoRowB } from './bandB';
import type { MatchRow, MatchState } from '../../types';

/**
 * B-8 — the **in-match takeover chrome** (design spec §2.2/§2.4): fixed bands whose element
 * **never unmounts during a match**. Every capture surface (Discovery B-9, the board B-10, the
 * break hub B-14, the sheets) is a middle band passed into this one component — so "the chrome is
 * identical on every surface" is a fact about one element's one stylesheet rule, not a promise
 * each screen keeps.
 *
 * ── B-31/§21.4 — the geometry contract, restated rather than quietly broken ──
 *
 * This header used to say *"three fixed bands whose geometry never changes"*. §21.4's ruling makes
 * that sentence false — the bottom band does not render during the adjustments workflow — so it is
 * replaced by the property that actually matters and is actually checkable:
 *
 * > `status`, `clock`, `band` and `strip` are same-y across every surface, including the workflow
 * > and the card. `bottom` is measured only where it renders, and `middle` is measured for its
 * > **top** edge only.
 *
 * The whistle cannot be affected, and that is a CSS-level guarantee rather than a convention:
 * `.capb` is a column flex container at `inset: 0`, `.capb-top` is its **first** `flex: none`
 * child, and no property of any later sibling participates in the whistle's y. `.capb-middle` is
 * `flex: 1 1 auto; min-height: 0`, so it absorbs the freed height **downward only** — its top edge
 * is pinned, which is why its height needs a clause and not a carve-out.
 *
 * ── The two state-bound globals live here, and only here ─────────────────────
 *
 * §2.2: on every `Match.state` change the chrome calls `wakeLock.syncWakeLock(state)` and
 * `recorder.syncToMatch(state)` **on the post-commit render, before the shell swap**. Both
 * effects are keyed on `match.state` and have **no cleanup that releases** — a mount-scoped
 * lock is the named defect (baseline §4.4: bound to `Match.state`, never to a mounted screen;
 * releasing on unmount would drop the hold on every surface navigation). Because the hand-off
 * effect below is declared **after** them, React runs them first within the same commit: at
 * `fullTime` the wake lock is released and any recording finalized while this chrome still
 * exists, and only then does the shell swap fire. `recorder.syncToMatch` is also AUD-3's
 * "any `Match.state` transition" finalize trigger — it runs as the post-commit effect because
 * finalizing awaits a `dataavailable` and an IndexedDB transaction dies on any non-IDB await.
 *
 * ── The hand-off, and why it is an effect on the engine's own predicate ──────
 *
 * `fullTime` is out-of-match **by engine law** (`FullTime`/`Abandon` clear `activeMatch`;
 * `isResumable` is preMatch ∨ isInMatch), so the chrome does not render a post-match surface —
 * it reports the boundary crossing to its host, which swaps to the tab shell landing on the
 * match record (§2.2). The predicate is `isResumable`, reused rather than re-derived, so the
 * launch-time shell selector (`resolveResume`) and the mid-session hand-off cannot disagree.
 */

/**
 * The half label in the top band, total over `MatchState` and keyed by `MATCH_STATE` (the
 * `ses-1/match-state-literals` rule refuses bare literals in `ui-b/`, and a tenth state breaks
 * this build instead of rendering `undefined` at a venue). The post-match rows exist only for
 * totality: this chrome hands off before any of them stays on screen.
 */
const HALF_LABEL_B: Record<MatchState, string> = {
  [MATCH_STATE.idle]: '—',
  [MATCH_STATE.preMatch]: 'Not started',
  [MATCH_STATE.firstHalf]: '1st half',
  [MATCH_STATE.halftime]: 'Half time',
  [MATCH_STATE.secondHalf]: '2nd half',
  [MATCH_STATE.fullTime]: 'Full time',
  [MATCH_STATE.triage]: 'Full time',
  [MATCH_STATE.distill]: 'Full time',
  [MATCH_STATE.archived]: 'Archived',
};

export interface CaptureChromeBProps {
  readonly match: MatchRow;
  readonly opponent: string;
  /** The one band that changes between surfaces. */
  readonly children: ReactNode;
  /**
   * B-19a — the user's LOCK-1 minute, read by the host with the match and handed straight to the
   * strip. The chrome does not interpret it; it is here because the strip is here.
   */
  readonly lockNudgeMin: number;
  /** Called after any transition commits — the committed row may have changed; re-read it. */
  readonly onTransitioned: () => void;
  /** §2.2's boundary crossing out of the takeover, fired AFTER the two syncs (see above). */
  readonly onLeftMatch: (matchId: string) => void;
  /** B-10 — the strip's lock-nudge action: the host opens the lock review surface. */
  readonly onLockNudge: () => void;
  /** B-11 — the undo pill's candidates: every hypothesis the shell knows (surfaces publish). */
  readonly undoRows: readonly UndoRowB[];
  /** B-11 — an undo committed; the shell re-reads its rows and the live surface's. */
  readonly onUndone: () => void;
  /** B-12 — the band's set-piece press: the shell opens the sheet over the middle band. */
  readonly onSetPiece: () => void;
  /** B-13 — the band's flags-review press: the shell shows the review as the middle band. */
  readonly onFlagsReview: () => void;
  /**
   * B-31/§21.4 — does the bottom band render at all? `false` for exactly one surface, the
   * adjustments workflow (every step, the card included), on the stakeholder's own ruling. The
   * shell decides; the chrome does not interpret. See `TakeoverShell`'s `showBottomBand` prop for
   * the ruling in his words.
   */
  readonly showBottomBand: boolean;
  /**
   * B-35/§21.5 — the finished coach card is the middle band right now. Handed straight to the
   * strip, which withholds the service-worker `update` notice (and only that one) while it is
   * true. The chrome does not interpret it, exactly as it does not interpret `lockNudgeMin`.
   *
   * **A second prop rather than a re-use of `showBottomBand`**, though both are false in the same
   * episode: §21.4 empties the band for *every* step of the workflow (an accidental tap is the
   * hazard), and R-7 withholds the banner only where it is noise — *the card being read aloud*.
   * One boolean serving both would silently widen whichever ruling moved next.
   */
  readonly cardShowing: boolean;
}

export function CaptureChromeB(props: CaptureChromeBProps) {
  const {
    match,
    opponent,
    lockNudgeMin,
    onTransitioned,
    onLeftMatch,
    onLockNudge,
    undoRows,
    onUndone,
    onSetPiece,
    onFlagsReview,
    showBottomBand,
    cardShowing,
  } = props;
  const clock = useCaptureClock(match);

  /**
   * ENV-8 — the wake lock follows `Match.state`. No cleanup that releases (see the header);
   * release happens when the *state* leaves the in-match grouping, which `syncWakeLock` decides.
   */
  useEffect(() => {
    void syncWakeLock(match.state).catch((cause: unknown) => {
      log.error('ui-b/capture', 'ENV-8: the screen wake lock could not be synchronised', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
    });
  }, [match.state]);

  /** §8.1 + AUD-3 — the microphone follows `Match.state`; a transition finalizes any recording. */
  useEffect(() => {
    recorder.syncToMatch(match.state);
  }, [match.state]);

  /**
   * The shell hand-off — declared after the two syncs so it runs after them in the same
   * commit. Firing more than once is harmless (the host's swap is idempotent setState), so the
   * deps are simply the row and the callback.
   */
  useEffect(() => {
    if (!isResumable(match)) onLeftMatch(match.id);
  }, [match, onLeftMatch]);

  return (
    <div className="capb" data-testid="capb" data-state={match.state}>
      <header className="capb-top">
        {/* Row 1 — the status line: one dominant numeral (skill §2/§11), opponent, half. */}
        <div className="capb-status">
          <span className="capb-clock" data-testid="capb-clock">
            {clock.minute}
            <span className="capb-clock__unit">′</span>
          </span>
          <span className="capb-status__title">
            <span className="capb-opponent" data-testid="capb-opponent">
              {opponent}
            </span>
            <span className="capb-half" data-testid="capb-half">
              {HALF_LABEL_B[match.state]}
            </span>
          </span>
        </div>

        {/* Row 2 — the guarded whistle beside the one-slot strip. The band's height is fixed in
            capture.css; when a state renders no hold the strip carries it alone. Top placement
            is deliberate (§2.4): whistle presses are rare and guarded — distance is part of the
            guard — and the overrun nudge renders beside the only control that answers it. */}
        <div className="capb-band" data-testid="capb-band">
          <div className="capb-transition" data-slot="transition" data-testid="capb-transition">
            <TransitionControlB match={match} onTransitioned={onTransitioned} />
          </div>
          <NotificationStripB
            match={match}
            now={clock.now}
            lockNudgeMin={lockNudgeMin}
            onLockNudge={onLockNudge}
            cardShowing={cardShowing}
          />
        </div>
      </header>

      <main className="capb-middle" data-testid="capb-middle">
        {props.children}
      </main>

      {/* The thumb zone (§2.4) — B-11's band, at the height B-8 reserved: Flag + recording
          chip, the undo pill overlay, the B-12/B-13 flank slots, and the guarded overflow.
          Abandon rides `onTransitioned` like every transition: the committed `fullTime` row
          re-renders this chrome, the two syncs above run (wake lock released, any recording
          finalized), and only then does the hand-off effect fire — one ordering for every way
          out of the match. */}
      {showBottomBand && (
        <BottomBandB
          match={match}
          undoRows={undoRows}
          onUndone={onUndone}
          onTransitioned={onTransitioned}
          onSetPiece={onSetPiece}
          onFlagsReview={onFlagsReview}
        />
      )}
    </div>
  );
}
